/******/ (() => { // webpackBootstrap
/* eslint-disable */
if (!customElements.get('product-information')) {
  customElements.define('product-information', class ProductInformation extends HTMLElement {
    constructor() {
      super();
      this.dataCache = new Map(); // Cache for pre-fetched data
      this.prefetchPromises = new Map(); // Track in-flight requests to avoid duplicate fetches
      this.isProcessingVariantChange = false; // Guard to prevent re-entrant variant handling loops
      // Cache the theme-wide translation payload once so every instance can reuse it without extra DOM work.
      if (!this.constructor.globalTranslations) {
        const globalEl = document.querySelector('script#wetheme-global');
        if (globalEl) {
          try {
            const parsedGlobals = JSON.parse(globalEl.textContent);
            this.constructor.globalTranslations = parsedGlobals?.translations || {};
          } catch (error) {
            this.constructor.globalTranslations = {};
          }
        } else {
          this.constructor.globalTranslations = {};
        }
      }
      this.translations = this.constructor.globalTranslations || {};
      this.init();
    }

    init() {
      this.cacheDOMElements();
      this.bindEventHandlers();
      this.continuousScroll();
    }

    cacheDOMElements() {
      /* ===== Cache any information that we'll need later ===== */
      this.sectionId = this.getAttribute('data-section-id');
      this.originalSectionId = this.getAttribute('data-original-section-id');
      this.isQuickView = this.dataset.quickView === 'true';
      this.productForm = this.querySelector('product-form');
      this.stickyContainer = this.classList.contains('product-sticky') ? this : null;
      this.stickyHeader = document.querySelector('.header-section:has(.header-is-sticky)');
      this.enableURLUpdate = this.dataset.enableUrlUpdate === 'true';
      this.mainProductURL = this.dataset.url;
    }

    connectedCallback() {
      /* ===== Gather options for the current variant, handle sticky form offset, attach event listeners ===== */
      this.setStickyOffset();
      this.attachEventListeners();
      this.isCombinedListing = this.dataset.isCombinedListing === 'true';

      const initialVariantId = this.currentVariant?.id || this.querySelector('[name="id"]')?.value;
      if (initialVariantId) this.updateVariantScopedNodes(initialVariantId);

      // Ensure an initially forced-out variant disables the CTA before the first interaction.
      this.syncInitialVariantState();
      // Fix soldout classes for non-color swatches on initial load.
      this.refreshSwatchSoldoutClasses();
    }

    disconnectedCallback() {
      /* ===== Clean up event listeners ===== */
      this.removeEventListeners();
    }

    bindEventHandlers() {
      /* ===== Bind methods to maintain the 'this' context ===== */
      this.resizeHandler = this.handleResize.bind(this);
      this.onQuantityChange = this.handleQuantityChange.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.prefetchVariantData = this.prefetchVariantData.bind(this);
      this.updateVariantScopedNodes = this.updateVariantScopedNodes.bind(this);
    }

    attachEventListeners() {
      // On resize we need to update the sticky form offset
      window.addEventListener('resize', this.resizeHandler);
      // Listen for quantity changes
      eventBus.on('qty:change', this.onQuantityChange);
      // Listen for variant changes
      eventBus.on('variant:change', this.onVariantChange);
      // Prefetch variant data
      this.addEventListener('mouseover', this.prefetchVariantData);
      this.addEventListener('touchstart', this.prefetchVariantData);
    }

    removeEventListeners() {
      /* ===== Remove any event listeners we've attached ===== */
      window.removeEventListener('resize', this.resizeHandler);
      eventBus.off('qty:change', this.onQuantityChange);
      eventBus.off('variant:change', this.onVariantChange);
      this.removeEventListener('mouseover', this.prefetchVariantData);
      this.removeEventListener('touchstart', this.prefetchVariantData);
    }

    prefetchVariantData() {
      const variants = this.querySelectorAll('[data-product-variant]');
      if (!variants.length) return;

      variants.forEach(variant => {
        const productFetchUrl = variant.getAttribute('data-product-fetch-url');

        if (productFetchUrl && !this.dataCache.has(productFetchUrl)) {
          this.fetchProductData(productFetchUrl).catch(error => {
            console.error(`Failed to fetch data for ${productFetchUrl}:`, error);
          });
        }
      });
    }

    async fetchProductData(productFetchUrl) {
      if (!productFetchUrl) return undefined;

      if (this.dataCache.has(productFetchUrl)) {
        return this.dataCache.get(productFetchUrl);
      }

      if (this.prefetchPromises.has(productFetchUrl)) {
        return this.prefetchPromises.get(productFetchUrl);
      }

      const requestPromise = (async () => {
        try {
          const response = await fetch(`${productFetchUrl}&section_id=${this.originalSectionId}`);
          if (!response.ok) {
            throw new Error(`Network response was not ok for ${productFetchUrl}`);
          }
          const data = await response.text();
          const productData = this.extractProductData(data);
          this.dataCache.set(productFetchUrl, productData);
          return productData;
        } catch (error) {
          console.error(`Failed to fetch product data from ${productFetchUrl}:`, error);
          return undefined;
        } finally {
          this.prefetchPromises.delete(productFetchUrl);
        }
      })();

      this.prefetchPromises.set(productFetchUrl, requestPromise);
      return requestPromise;
    }

    async fetchVariantData(productFetchUrl, context) {
      /* ===== Fetch the product data if it's not already cached ===== */
      if (!this.dataCache.has(productFetchUrl)) {
        await this.fetchProductData(productFetchUrl);
      }
      this.updateDOMWithData(context);
    }

    extractProductData(data) {
      /* ===== Parse the fetched data and return the new product data ===== */
      const parser = new DOMParser();
      const htmlDocument = parser.parseFromString(data, 'text/html');
      const productInformation = htmlDocument.querySelector('product-information');
      const quickViewContent = htmlDocument.querySelector('template[data-quick-view-product]')?.content;

      const quickViewContentInformation = quickViewContent?.querySelector('product-information');
      const productDataElement = this.isQuickView && quickViewContentInformation ? quickViewContentInformation : productInformation;

      const productMedia = htmlDocument.querySelector('[data-product-media-wrapper]');
      const quickViewMedia = quickViewContent?.querySelector('[data-product-media-wrapper]');
      const productBadges = this.isQuickView && quickViewMedia ? quickViewMedia.querySelectorAll('[data-product-badge]') : productMedia?.querySelectorAll('[data-product-badge]');

      if (this.isCombinedListing || productBadges.length) {
        const productMediaElement = this.isQuickView && quickViewMedia ? quickViewMedia : productMedia;
        const productDiv = document.createElement('div');
        productDiv.setAttribute('data-url', productInformation?.getAttribute('data-url'));
        productDiv.appendChild(productMediaElement);
        productDiv.appendChild(productDataElement);

        return productDiv.outerHTML;
      }

      return productDataElement.outerHTML;
    }

    handleResize() {
      /* ===== Handle resizing of the window ===== */
      if (window.Shopify.designMode) {
        // If we're in the theme editor we need to update the sticky form offset
        // We only do this in the editor because it's not necessary in the live site
        // because mobile devices can incorrectly trigger resize events when scrolling
        this.setStickyOffset();
      }
    }

    handleQuantityChange(event) {
      /* ===== Handle changes to the quantity input ===== */

      // Update the quantity input value
      this.productForm = this.querySelector('product-form');
      this.formQuantityInput = this.productForm?.querySelector('[name="quantity"]');

      if (this.formQuantityInput && event.value) {
        if (event.sectionId && event.sectionId !== this.sectionId) return;
        this.formQuantityInput.value = event.value;
      }
    }

    setStickyOffset() {
      if (this.stickyContainer && this.stickyHeader) {
        // Set the top offset of the sticky form to the height of the sticky header
        this.stickyContainer.style.top = `${this.stickyHeader.offsetHeight + 30}px`;
      }
    }

    async handleVariantChange(context) {
      if (this.isProcessingVariantChange) return;
      this.isProcessingVariantChange = true;

      try {
        /* ===== Handle changes to the variant ===== */
        if (context.sectionId && context.sectionId !== this.sectionId) return;

        // If the variant change was triggered by the media and the variant doesn't match
        // any of the options, we don't need to update the UI
        if (context.fromSlideChange) {
          const mediaVariantId = this.querySelector(`[data-swatch-variant-id="${context.variant.id}"]`) || this.querySelector(`[data-dropdown-variant-id="${context.variant.id}"]`);
          if (!mediaVariantId) return;
        }

        // Update the current variant based on the selected options
        this.currentVariant = context.variant;
        this.productFetchUrl = context.fetchURL;
        this.productUrl = context.productURL;

        // If we don't have a valid variant, handle it and return
        if (!this.currentVariant) {
          this.handleInvalidVariant();
          return;
        }

        if (!this.productFetchUrl || !this.productUrl) return;

        // If the variant is available, update the UI
        // Re-evaluate the purchase CTA any time we receive fresh variant data.
        this.toggleAddButton(this.currentVariant.available, this.currentVariant);
        const isCached = this.dataCache.has(this.productFetchUrl);

        if (isCached && !window.Shopify.designMode) {
          this.updateDOMWithData(context);
        } else {
          // If the data is not cached, fetch the product data and then update the UI
          await this.fetchVariantData(this.productFetchUrl, context);
        }

        // Refocus on the active radio input
        const activeRadioInput = this.querySelector('input[type="radio"][name^="option-"]:checked');
        if (activeRadioInput && !context.fromSlideChange) {
          activeRadioInput.focus();
        }
      } finally {
        this.isProcessingVariantChange = false;
      }
    }

    handleInvalidVariant() {
      /* ===== Handle an invalid variant ===== */
      const productFetchUrl = this.buildRequestUrlWithParams();
      this.fetchNullVariantData(productFetchUrl);

      // If the variant is invalid, disable the add to cart button
      this.toggleAddButton(false);
      // If the variant is invalid, set the add to cart button text to "unavailable"
      const addButton = this.productForm?.querySelector('[name="add"]');
      if (!addButton) return;
      const addButtonSpan = addButton.querySelector('[data-add-to-cart-text]');
      if (!addButtonSpan) return;

      addButtonSpan.textContent = addButtonSpan.getAttribute('data-unavailable-text');

      // If the variant is invalid, set the price to "Unavailable"
      const priceElement = this.querySelector('[data-product-price]');
      if (!priceElement) return;
      const priceElementSpan = priceElement.querySelector('span[data-price-text]');
      if (!priceElementSpan) return;

      priceElementSpan.textContent = priceElementSpan.getAttribute('data-unavailable-text');
    }

    buildRequestUrlWithParams(shouldFetchFullPage = false) {
      const params = [];
      const optionValues = this.selectedOptionValues;

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${this.mainProductURL}?${params.join('&')}`;
    }

    get selectedOptionValues() {
      return Array.from(this.querySelectorAll('li[data-product-variant][selected], input[data-product-variant]:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
    }

    async fetchNullVariantData(productFetchUrl) {
      /* ===== Fetch the product data if it's not already cached ===== */
      try {
        const response = await fetch(`${productFetchUrl}`);
        if (!response.ok) {
          throw new Error(`Network response was not ok for ${productFetchUrl}`);
        }
        const data = await response.text();
        const productData = this.extractProductData(data);
        this.dataCache.set(productFetchUrl, productData);
        this.updateNullVariant(data);
      } catch (error) {
        console.error(`Failed to fetch product data from ${productFetchUrl}:`, error);
      }
    }

    updateNullVariant(data) {
      const html = this.parseHTML(data);
      const productInformation = html.querySelector('product-information');
      
      // Replace the product options with the new data
      this.updateOptions(html, { productURL: productInformation?.getAttribute('data-url') });
    }

    updateDOMWithData(context) {
      /* ===== Update the UI with the cached data ===== */
      if (this.enableURLUpdate) this.updateURL();

      const data = this.dataCache.get(context.fetchURL);
      const html = this.parseHTML(data);
      this.updateDOM(html, context);
    }

    parseHTML(data) {
      /* ===== Parse the fetched data into a DOM object ===== */
      return new DOMParser().parseFromString(data, 'text/html');
    }

    updateDOM(html, context = {}) {
      /* ===== Update the DOM with the fetched data ===== */
      this.replaceElements(html, context);

      if (this.currentVariant?.id) {
        this.updateVariantScopedNodes(this.currentVariant.id);
      }

      // Keep the CTA text in sync after we swap DOM nodes via fetch.
      if (this.currentVariant) {
        this.toggleAddButton(this.currentVariant.available, this.currentVariant);
      }
      // Fix soldout classes for non-color swatches after DOM swap.
      this.refreshSwatchSoldoutClasses();

      if (context.fromSlideChange) return;

      // Emit an event to let other components know the variant was updated
      eventBus.emit('variant:updated', {
        variant: this.currentVariant,
        sectionId: this.sectionId,
        isCombinedListing: context.isCombinedListing
      });
    }

    toggleAddButton(enable, variant = null) {
      /* ===== Toggle the add to cart button based on the variant availability ===== */
      const addButton = this.productForm?.querySelector('[name="add"]');
      if (addButton) {
        const isForcedSoldOut = variant?.force_out_of_stock === true || this.isCurrentSelectionForcedOutOfStock();
        const shouldEnable = enable && !isForcedSoldOut;

        addButton.toggleAttribute('disabled', !shouldEnable);
        addButton.setAttribute('aria-disabled', String(!shouldEnable));
        const addButtonSpan = addButton.querySelector('[data-add-to-cart-text]');
        if (addButtonSpan) {
          if (!addButtonSpan.dataset.originalText) {
            addButtonSpan.dataset.originalText = addButtonSpan.textContent.trim();
          }

          // Prefer the global translation, falling back to the data attribute or current label.
          const soldOutText = this.getTranslation('soldOut', this.translations?.soldOut || addButtonSpan.dataset.soldOutText || addButtonSpan.textContent.trim());

          if (!shouldEnable && isForcedSoldOut) {
            addButtonSpan.textContent = soldOutText;
            addButton.setAttribute('aria-label', soldOutText);
          } else if (shouldEnable && addButtonSpan.dataset.originalText) {
            addButtonSpan.textContent = addButtonSpan.dataset.originalText;
            addButton.setAttribute('aria-label', addButtonSpan.dataset.originalText);
          } else if (!shouldEnable && addButtonSpan.dataset.originalText) {
            // For genuine sold-out variants fall back to the original "Sold out" snippet text if available.
            addButtonSpan.textContent = soldOutText;
            addButton.setAttribute('aria-label', soldOutText);
          }
        }
      }
    }

    // Small helper to safely read from the cached translation map.
    getTranslation(key, fallback = '') {
      if (!key) return fallback;
      if (this.translations && Object.prototype.hasOwnProperty.call(this.translations, key) && this.translations[key]) {
        return this.translations[key];
      }
      return fallback;
    }

    refreshSwatchSoldoutClasses() {
      const variantsScript = this.querySelector('script[data-all-variants]');
      if (!variantsScript) return;

      let allVariants;
      try {
        allVariants = JSON.parse(variantsScript.textContent).variants;
      } catch (e) {
        return;
      }
      if (!allVariants?.length) return;

      // Only target non-color product-swatch elements (the ones the CSS soldout line applies to).
      const nonColorSwatches = this.querySelectorAll('product-swatch:not(.is-color-swatch) .swatch-element[data-option]');
      nonColorSwatches.forEach((element) => {
        const optionName = element.getAttribute('data-option');
        const input = element.querySelector('input[type="radio"]');
        if (!optionName || !input) return;

        const optionValue = input.value || input.getAttribute('data-option-value');
        if (!optionValue) return;

        const anyAvailable = allVariants.some(
          v => v.options[optionName] === optionValue && v.available === true && v.force_out_of_stock !== true && v.force_out_of_stock !== 'true'
        );
        element.classList.toggle('available', anyAvailable);
        element.classList.toggle('soldout', !anyAvailable);
      });
    }

    isCurrentSelectionForcedOutOfStock() {
      const checkedInput = this.querySelector('input[data-product-variant]:checked');
      if (checkedInput?.getAttribute('data-force-out-of-stock') === 'true') {
        return true;
      }
      const selectedListItem = this.querySelector('li[data-product-variant][selected]');
      if (selectedListItem?.getAttribute('data-force-out-of-stock') === 'true') {
        return true;
      }
      return false;
    }

    syncInitialVariantState() {
      const checkedInput = this.querySelector('input[data-product-variant]:checked');
      const dropdownSelected = this.querySelector('li[data-product-variant][selected]');
      const forceOutOfStock = checkedInput?.getAttribute('data-force-out-of-stock') === 'true' || dropdownSelected?.getAttribute('data-force-out-of-stock') === 'true';
      if (!forceOutOfStock) return;

      const initialVariant = Object.assign({}, this.currentVariant || {});
      initialVariant.available = false;
      initialVariant.force_out_of_stock = true;
      this.toggleAddButton(false, initialVariant);
    }

    updateVariantScopedNodes(variantId) {
      const nodes = this.querySelectorAll('[data-variant-scope][data-variant-id]');
      const vid = String(variantId);

      nodes.forEach(node => {
        const matches = node.dataset.variantId === vid;
        node.toggleAttribute('hidden', !matches);
        node.setAttribute('aria-hidden', String(!matches));
      });
    }

    updateURL() {
      /* ===== Update the URL with the current variant ID ===== */
      if (this.currentVariant) {
        window.history.replaceState({}, '', `${this.productFetchUrl}`);
      }
    }

    updateOptions(html, context = {}) {
      /* ===== Update the product options with the new data ===== */
      if (!html) return;

      const productInformation = html.querySelector(`product-information[data-url="${context.productURL}"]`);
      if (!productInformation) return;
    
      const currentElements = Array.from(this.querySelectorAll('[data-product-options]'));

      // Update existing options
      currentElements.forEach(currentElement => {
        const currentUpdateId = currentElement.getAttribute('data-update-id');
        const newElement = productInformation.querySelector(`[data-update-id="${currentUpdateId}"]`);
        if (newElement) {
          // Replace the element if it has the data-replace-content attribute, otherwise update the innerHTML
          currentElement.replaceWith(newElement);
        }
      });
    }

    replaceElements(html, context = {}) {
      /* ===== Replace elements in the DOM with new elements ===== */
      if (!html) return;
    
      const productInformation = html.querySelector(`product-information[data-url="${context.productURL}"]`);
      if (!productInformation) return;

      const currentElements = Array.from(this.querySelectorAll('[data-update-id]'));
      const newElements = Array.from(productInformation.querySelectorAll('[data-update-id]'));
    
      // Update existing elements and track their order
      currentElements.forEach(currentElement => {
        const currentUpdateId = currentElement.getAttribute('data-update-id');
        const newElement = productInformation.querySelector(`[data-update-id="${currentUpdateId}"]`);
        if (newElement) {
          // Replace the element if it has the data-replace-content attribute, otherwise update the innerHTML
          newElement.hasAttribute('data-replace-content') ? currentElement.replaceWith(newElement) : currentElement.innerHTML = newElement.innerHTML;
        }
      });
    
      // Add new elements in the correct order
      newElements.forEach(newElement => {
        const newUpdateId = newElement.getAttribute('data-update-id');
        const existingElement = this.querySelector(`[data-update-id="${newUpdateId}"]`);
    
        if (!existingElement) {
          // Determine the correct position to insert the new element
          let inserted = false;
          for (let i = 0; i < currentElements.length; i++) {
            const currentElement = currentElements[i];
            const currentUpdateId = currentElement.getAttribute('data-update-id');
            if (currentUpdateId > newUpdateId) {
              currentElement.insertAdjacentElement('beforebegin', newElement.cloneNode(true));
              inserted = true;
              break;
            }
          }
          // If not inserted, append to the end
          if (!inserted) {
            this.appendChild(newElement.cloneNode(true));
          }
        }
      });

      const productMedia = html.querySelector('[data-product-media-wrapper]');
      const productWrapper = this.closest('[data-product-content-wrapper]');
      const currentProductMedia = productWrapper?.querySelector('[data-product-media-wrapper]');

      // Update the badges if they exist
      const currentBadges = currentProductMedia ? currentProductMedia.querySelectorAll('[data-product-badge]') : null;
      const newBadges = productMedia ? productMedia.querySelectorAll('[data-product-badge]') : null;

      if (currentBadges && newBadges) {
        currentBadges.forEach((badge, index) => {
          const newBadge = newBadges[index];
          if (newBadge) {
            badge.replaceWith(newBadge);
          }
        });
      }
    
      if (this.isCombinedListing && context.isCombinedListing) {
        if (productMedia && currentProductMedia) currentProductMedia.replaceWith(productMedia);
      }
    
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }
    }    

    continuousScroll() {
      // Continuous scroll
      const targetElement = this;
      let isScrollingTarget = false;

      const handleWheelScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = targetElement;

        if (scrollTop === 0 && e.deltaY < 0) {
          // Scrolled to the top and trying to scroll up
          e.preventDefault();
          window.scrollBy({ top: e.deltaY });
        } else if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
          // Scrolled to the bottom and trying to scroll down
          e.preventDefault();
          window.scrollBy({ top: e.deltaY });
        }
      };

      targetElement.addEventListener('wheel', handleWheelScroll, { passive: false });
    }
  });
}
/******/ })()
;
